import { createContext, useContext, useEffect } from 'react';
import type { DocumentExchangeResponse } from 'src/apis/document-exchange/types';
import { useAxios } from 'src/hooks/use-axios';
import { handleError } from 'src/utils/handle-error';
import { reportToSentry } from 'src/utils/report-to-sentry';

export function useDocumentExchangeRoot() {
  const documentExchange = useAxios(
    (axios, baseConfig) =>
      axios.request<DocumentExchangeResponse>({
        ...baseConfig,
        url: PRISMA_CONFIG.documentExchange,
      }),
    {
      neededOnPageLoad: true,
      cacheKey: 'document-exchange-root',
    }
  );

  useEffect(() => {
    documentExchange.execute();
    // oxlint-disable-next-line exhaustive-deps
  }, []);

  return documentExchange;
}

export function useOptionalDocumentExchangeRequest() {
  const optionalDocumentExchange = useAxios(
    (axios, baseConfig) =>
      axios.request<DocumentExchangeResponse>({
        ...baseConfig,
        url: PRISMA_CONFIG.documentExchange,
        timeout: 5000,
      }),
    {
      neededOnPageLoad: true,
      onError(error) {
        const [_, report] = handleError(error, () => {});
        if (report) reportToSentry(error);
        return error;
      },
    }
  );

  useEffect(() => {
    optionalDocumentExchange.execute();
    // oxlint-disable-next-line exhaustive-deps
  }, []);

  return optionalDocumentExchange;
}

export type DocumentExchangeRequest = ReturnType<
  typeof useDocumentExchangeRoot
>;

const DocumentExchangeContext = createContext<
  DocumentExchangeResponse | undefined
>(undefined);

export const DocumentExchangeProvider = DocumentExchangeContext.Provider;

export function useDocumentExchange() {
  return useContext(DocumentExchangeContext);
}
